import { ArrowLeft } from "lucide-react"
import { useWizard } from "./WizardContext"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const complexityOptions = [
  {
    value: "simple" as const,
    label: "Simple",
    description: "Basic gable or hip roof with few planes",
    svg: (
      <svg viewBox="0 0 120 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M10 65 L60 20 L110 65" />
        <path d="M10 65 L110 65" />
      </svg>
    ),
  },
  {
    value: "normal" as const,
    label: "Moderate",
    description: "Multiple gables, valleys, or dormers",
    svg: (
      <svg viewBox="0 0 120 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M10 65 L35 30 L55 50 L80 20 L110 65" />
        <path d="M10 65 L110 65" />
      </svg>
    ),
  },
  {
    value: "complex" as const,
    label: "Complex",
    description: "Many planes, steep pitch, turrets, or unusual shapes",
    svg: (
      <svg viewBox="0 0 120 80" className="w-full h-full" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M10 65 L25 40 L40 50 L55 25 L65 35 L80 15 L95 40 L110 65" />
        <path d="M10 65 L110 65" />
      </svg>
    ),
  },
]

export default function ComplexityStep() {
  const { state, setComplexity, nextStep, prevStep } = useWizard()

  const handleSelect = (value: "simple" | "normal" | "complex") => {
    setComplexity(value)
    nextStep()
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground tracking-tight">
          How would you describe your roof?
        </h2>
        <p className="text-muted-foreground mt-1">
          This helps us estimate material waste and labor complexity.
        </p>
      </div>

      <div className="grid gap-4">
        {complexityOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={cn(
              "flex items-center gap-5 p-5 border-2 rounded-xl text-left transition-all duration-200 hover:border-primary/50 hover:bg-primary/[0.02] group",
              state.complexity === option.value
                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                : "border-muted",
            )}
          >
            <div
              className={cn(
                "w-20 h-14 flex-shrink-0 transition-colors",
                state.complexity === option.value ? "text-primary" : "text-muted-foreground group-hover:text-primary/70",
              )}
            >
              {option.svg}
            </div>
            <div>
              <div className="font-bold text-foreground text-[15px]">{option.label}</div>
              <div className="text-sm text-muted-foreground mt-0.5">{option.description}</div>
            </div>
          </button>
        ))}
      </div>

      <Button
        variant="outline"
        onClick={prevStep}
        className="h-12 px-6 rounded-xl font-semibold"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>
    </div>
  )
}
